import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { get, post, withPath, type ApiData } from '@app/api/typed';
import type { Decision } from '@app/main/Decisions';
import type { AgentCallResult, DecisionState, LabPerson } from './ConverseModel';

/** Capabilities da identidade — de onde saem as tools do combobox. */
export type IdentityCapabilities = ApiData<'/api/v1/identities/{id}/capabilities'>;

export const converseKeys = {
  pessoas: () => ['agentes', 'pessoas'] as const,
  capabilities: (identityId: string) => ['identities', identityId, 'capabilities'] as const,
  decisao: (chave: string) => ['agentes', 'decisao', chave] as const,
};

/**
 * Elenco do lab (`GET /api/v1/agents/people`).
 *
 * `staleTime` alto: o elenco é fixo no Rego do cliente e não muda durante uma
 * demonstração. O `exp` do token envelhece, mas ele é informativo — quem o
 * renova é o lab, não esta tela.
 */
export function useLabPeople(): UseQueryResult<LabPerson[], ApiError> {
  return useQuery<LabPerson[], ApiError>({
    queryKey: converseKeys.pessoas(),
    queryFn: () => get('/api/v1/agents/people'),
    staleTime: 5 * 60_000,
  });
}

/** Capabilities de uma identidade — só quando há identidade escolhida. */
export function useCapabilities(
  identityId: string | null,
): UseQueryResult<IdentityCapabilities, ApiError> {
  return useQuery<IdentityCapabilities, ApiError>({
    queryKey: converseKeys.capabilities(identityId ?? ''),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/capabilities', {
          id: identityId ?? '',
        }) as '/api/v1/identities/{id}/capabilities',
      ),
    enabled: Boolean(identityId),
    staleTime: 30_000,
  });
}

/**
 * Todas as tools do catálogo, pelo nome (30/08, pedido do mrolim). O nome é um só, o do
 * gateway, e é o que o PDP vê (D2 do aim-cpj).
 *
 * O seletor de tool desta tela listava só o que a identidade tem — e uma tool
 * recém-dada por política ou por contrato novo só aparecia recarregando a
 * página, o que apaga a conversa. A lista passa a ser o catálogo inteiro, e o
 * `staleTime` curto faz uma tool nova entrar sem reload, no ritmo da própria
 * demonstração.
 */
export function useToolsCatalog(): UseQueryResult<string[], ApiError> {
  return useQuery<string[], ApiError>({
    queryKey: ['agentes', 'catalogo-de-tools'] as const,
    staleTime: 15_000,
    queryFn: async () => {
      const page = await get('/api/v1/resources', { params: { page: 1, size: 200 } });
      // O item da lista é aberto no contrato; o recorte aqui é só o que se lê.
      const itens = (page.items ?? []) as { tools?: { gateway_name?: string | null }[] }[];
      const names = itens.flatMap((item) =>
        (item.tools ?? [])
          .map((tool) => tool.gateway_name ?? '')
          .filter((nome): nome is string => Boolean(nome)),
      );
      return [...new Set(names)].sort((a, b) => a.localeCompare(b, 'pt-BR'));
    },
  });
}

/** Um envio da tela: quem chamou o quê, e por quem. */
export interface CallSend {
  /** Slug do agente que recebe o pedido. */
  agente: string;
  /** Slug do especialista, quando é delegação. Vazio = chamada direta. */
  delegado: string;
  pessoa: string;
  tool: string;
}

/**
 * Dispara a chamada (ou a delegação).
 *
 * É `useMutation`, e não `useQuery`, pela razão mais forte que existe para essa
 * escolha: **a chamada tem efeito**. Uma tool executa no MCP server do cliente;
 * revalidar sozinho, como uma query faz, executaria de novo sem ninguém pedir.
 */
export function useTriggerCall(): UseMutationResult<AgentCallResult, ApiError, CallSend> {
  return useMutation<AgentCallResult, ApiError, CallSend>({
    mutationFn: (envio) =>
      envio.delegado
        ? post(
            withPath('/api/v1/agents/{slug}/delegate', {
              slug: envio.agente,
            }) as '/api/v1/agents/{slug}/delegate',
            { body: { person: envio.pessoa, agent: envio.delegado, tool: envio.tool } },
          )
        : post(
            withPath('/api/v1/agents/{slug}/call', {
              slug: envio.agente,
            }) as '/api/v1/agents/{slug}/call',
            { body: { person: envio.pessoa, tool: envio.tool } },
          ),
  });
}

/** Intervalo entre consultas ao log de decisões. */
export const DECISION_MS_INTERVAL = 15_000;

/**
 * Por quanto tempo a tela procura a decisão antes de desistir.
 *
 * Dez minutos porque é o atraso observado no lab: o veredito passa pelo Log
 * Analytics e chegou entre 5 e 10 min nas medições de 29/08. Desistir antes
 * mostraria «não encontrada» sobre uma decisão que ainda vem.
 */
export const DECISION_MS_DEADLINE = 10 * 60_000;

// `EstadoDaDecisao` mora em `ConversarModel`: é o **mesmo** estado que o canal
// WebSocket produz por evento, e dois tipos com os mesmos três casos deixariam a
// tela ter de escolher qual desenhar por transporte.

export interface DecisionSearch {
  /** Chave estável do envio — troca a cada chamada nova. */
  chave: string;
  /** Slug do agente que executou (é o `application` da decisão). */
  aplicacao: string;
  tool: string;
  /** O `sub` de quem chamou: duas pessoas na mesma tool são duas decisões. */
  pessoa: string;
  /** O status que a chamada devolveu — um 2xx não pode ter decisão `negado`. */
  statusHttp: number;
  /** Instante do envio: só decisões **depois** dele contam. */
  desde: string;
}

/**
 * A decisão **desta** chamada, dentre as que o log devolveu na janela.
 *
 * ## O que se mediu (30/08)
 *
 * O casamento era `application` + `tool`, e esse par **não identifica uma
 * chamada**. Medido no log, janela de 24 h: seis pares `(application, tool)` com
 * mais de uma decisão, e `(cobranca, faturas.consultar)` sozinho com **seis** — de
 * três pessoas (`ana`, `bruna`, `carlos`) e com vereditos diferentes
 * (`permitido`, `negado`, `indeterminado`). O que separava uma da outra era só a
 * janela `desde`.
 *
 * E a janela não segura sozinha. Medido pelo mrolim no `.io` (`3ffdf39`): uma
 * decisão `negado` de `19:48:19` foi devolvida numa janela que começava em
 * `19:58:32` — a fonte filtra por **ingestão** e o item carrega o `ts` do
 * **evento**, então o DENY de dez minutos antes entrou. Resultado na tela: «DENY ·
 * ver em Decisões» sobre chamadas que devolveram 200.
 *
 * **O gatilho não foi reproduzido contra o mock** (ver `docs/progress/demo2.md`).
 * O que este código conserta não é o gatilho: é a ambiguidade que faz qualquer
 * gatilho virar um veredito errado na tela.
 *
 * ## Os filtros que faltavam
 *
 * `ts` — a janela se confere **aqui**, e não só se pede à fonte. Os dois lados são
 * RFC 3339 em UTC (`new Date().toISOString()` de um lado, `format: date-time` do
 * contrato do outro), e nesse formato a ordem lexicográfica é a cronológica.
 *
 * `sub` — a pessoa faz parte da identidade da chamada. A decisão da `ana` não
 * responde pela chamada da `bruna`, e no log elas só diferem por esse campo. Item
 * sem `sub` não é atribuível a ninguém, e por isso não casa: ficar em «o registro
 * chega em alguns minutos» é melhor do que mostrar o veredito de outro.
 *
 * `negado` num 2xx é **impossível**, e não improvável: quem aplica a decisão do
 * PDP é o gateway, então o que ele deixou passar com 200 não foi negado. Uma
 * decisão `negado` na janela de uma chamada que deu 200 é, necessariamente, de
 * outra chamada.
 *
 * O contrário **não** vale, e é de propósito: `permitido` numa chamada que tomou
 * 403 é anomalia de verdade — o gateway barrando o que o PDP concedeu —, e é
 * exatamente o tipo de coisa que esta tela existe para mostrar. Filtrá-la
 * esconderia o achado.
 *
 * ## Entre as que casam, a MAIS ANTIGA posterior ao envio
 *
 * A página vem mais recente primeiro (contrato `DecisionPage`), então pegar a
 * primeira seria pegar a mais nova — e isso erra na sequência que a demonstração
 * faz de propósito: mesma pessoa, mesma tool, `403` → concede a política → `200`,
 * com um ou dois minutos entre uma e outra.
 *
 * Nessa sequência as duas decisões casam com a busca da **primeira** chamada (a
 * janela dela contém as duas, e num `403` o `permitido` não é filtrado, porque é
 * a anomalia que a tela existe para mostrar). Pegando a mais nova, a chamada que
 * tomou `403` exibiria o ALLOW da chamada **seguinte** — e ainda por cima com cara
 * de achado. A primeira decisão depois do envio é, quase certamente, a da própria
 * chamada.
 */
export function callDecision(itens: Decision[], search: DecisionSearch): Decision | null {
  const candidatas = itens.filter((item) => callIs(item, search));
  if (candidatas.length === 0) return null;
  return candidatas.reduce((maisAntiga, item) => (item.ts < maisAntiga.ts ? item : maisAntiga));
}

function callIs(item: Decision, search: DecisionSearch): boolean {
  if (item.application !== search.aplicacao || item.tool !== search.tool) return false;
  if (item.ts < search.desde) return false;
  if (!item.sub || item.sub !== search.pessoa) return false;
  if (callFoi2xx(search.statusHttp) && item.verdict === 'negado') return false;
  return true;
}

function callFoi2xx(status: number): boolean {
  return status >= 200 && status <= 299;
}

/**
 * Procura no log a decisão daquela identidade + tool, a partir do horário do
 * envio (`GET /api/v1/decisions`, a rota que T16 já usa).
 *
 * É a alternativa do caminho REST: pelo canal quem espera é o servidor, e o
 * veredito chega no evento `decisao`. As duas pontas concordam sobre o que
 * «achou» significa — mesma janela (`desde`), mesmo filtro por tool, mesmo
 * casamento por `application`.
 *
 * ## Por que é polling, e não a resposta da chamada
 *
 * O veredito **não volta** na chamada: o executor responde `decisao_esperada_em:
 * "log"` porque a decisão nasce no log do gateway e atravessa o Log Analytics.
 * Ler o `status_http` como se fosse o veredito seria quase sempre certo e
 * ocasionalmente falso — um 403 pode vir do gateway sem o PDP ter sido
 * consultado —, e é justamente a diferença que esta tela existe para mostrar.
 *
 * ## Por que o filtro é por tool e não por identidade
 *
 * `identity_id` exigiria resolver o UUID da identidade que **executou** (na
 * delegação, o delegado). O `application` da decisão é o slug, e é ele que a
 * resposta da chamada traz — filtra-se por `tool` no servidor e casa-se o resto
 * aqui, sobre uma página pequena. Quem faz o casamento é `decisaoDaChamada`, e o
 * porquê de cada filtro está lá.
 */
export function useCallDecision(search: DecisionSearch | null): DecisionState {
  const [estado, setEstado] = useState<DecisionState>({ estado: 'aguardando' });
  // O prazo é contado do envio, e não do mount: remontar a tela não reinicia a
  // espera de uma chamada que já é velha.
  const startRef = useRef<{ chave: string; em: number } | null>(null);

  const chave = search?.chave ?? '';
  useEffect(() => {
    if (!chave) return;
    startRef.current = { chave, em: Date.now() };
    setEstado({ estado: 'aguardando' });
  }, [chave]);

  const expirado =
    startRef.current?.chave === chave &&
    Date.now() - (startRef.current?.em ?? 0) > DECISION_MS_DEADLINE;

  const query = useQuery({
    queryKey: converseKeys.decisao(chave),
    enabled: Boolean(search) && estado.estado === 'aguardando' && !expirado,
    refetchInterval: DECISION_MS_INTERVAL,
    // Sem cache entre envios: cada chamada procura a sua decisão.
    gcTime: 0,
    queryFn: async () => {
      if (!search) return null;
      const page = await get('/api/v1/decisions', {
        params: { since: search.desde, limit: 50, tool: search.tool },
      });
      // `Paginacao.itens` é `unknown[]` no contrato gerado e a especialização por
      // operação entra por interseção — o estreitamento fica aqui, num ponto só
      // (o mesmo de `useIdentidades`).
      const itens = (page.items ?? []) as Decision[];
      return callDecision(itens, search);
    },
  });

  useEffect(() => {
    const found = query.data;
    if (found) {
      setEstado({
        estado: 'encontrada',
        requestId: found.request_id,
        veredito: found.verdict,
      });
    }
  }, [query.data]);

  useEffect(() => {
    if (expirado && estado.estado === 'aguardando') setEstado({ estado: 'nao_encontrada' });
  }, [expirado, estado.estado]);

  return search ? estado : { estado: 'aguardando' };
}
